import { Capacitor } from "@capacitor/core";
import { FirebaseAnalytics } from "@capacitor-firebase/analytics";
import { FirebaseCrashlytics } from "@capacitor-firebase/crashlytics";
import { getAnalytics, isSupported, logEvent as logWebEvent, setAnalyticsCollectionEnabled, type Analytics } from "firebase/analytics";
import { firebaseApp } from "../firebase/client";
import { appEnvironment } from "../config/env";
import { isNative } from "./platform";

const SAFE_PARAM_KEYS = new Set([
  "screen",
  "action",
  "result",
  "platform",
  "plan",
  "feature",
  "environment",
  "source",
  "target",
  "mode",
  "status",
  "item_type",
  "step",
  "count_bucket",
  "duration_bucket"
]);

let webAnalyticsPromise: Promise<Analytics | null> | null = null;
let analyticsConsentEnabled = false;
let sessionStarted = false;
let sessionStartedAt = 0;
let lastLifecycleState = "foreground";

function currentPlatform(): string {
  return Capacitor.getPlatform() || (isNative() ? "native" : "web");
}

function safeEventName(name: string): string {
  const clean = String(name || "event")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
  return /^[a-z]/.test(clean) ? clean : `m_${clean || "event"}`.slice(0, 40);
}

function cleanValue(value: string | number | boolean): string | number | boolean {
  if (typeof value !== "string") return value;
  return value.trim().slice(0, 80);
}

function safeParams(params?: Record<string, string | number | boolean>): Record<string, string | number | boolean> | undefined {
  const merged = {
    ...(params || {}),
    platform: currentPlatform(),
    environment: appEnvironment
  };
  const entries = Object.entries(merged)
    .filter(([key]) => SAFE_PARAM_KEYS.has(key))
    .map(([key, value]) => [key, cleanValue(value)] as const);
  return entries.length ? Object.fromEntries(entries) : undefined;
}

function durationBucket(milliseconds: number): string {
  if (milliseconds < 10_000) return "lt_10s";
  if (milliseconds < 30_000) return "10_30s";
  if (milliseconds < 60_000) return "30_60s";
  if (milliseconds < 180_000) return "1_3m";
  if (milliseconds < 600_000) return "3_10m";
  return "10m_plus";
}

export function countBucket(count: number): string {
  if (count <= 0) return "0";
  if (count === 1) return "1";
  if (count <= 3) return "2_3";
  if (count <= 10) return "4_10";
  return "11_plus";
}

async function getWebAnalytics(): Promise<Analytics | null> {
  if (isNative()) return null;
  if (!webAnalyticsPromise) {
    webAnalyticsPromise = isSupported()
      .then((supported) => supported ? getAnalytics(firebaseApp) : null)
      .catch(() => null);
  }
  return webAnalyticsPromise;
}

export async function setAnalyticsConsent(enabled: boolean): Promise<void> {
  analyticsConsentEnabled = enabled;
  if (isNative()) {
    await FirebaseAnalytics.setEnabled({ enabled }).catch(() => undefined);
    return;
  }
  const analytics = await getWebAnalytics();
  if (analytics) setAnalyticsCollectionEnabled(analytics, enabled);
}

export async function logTechnicalEvent(name: string, params?: Record<string, string | number | boolean>): Promise<void> {
  if (!analyticsConsentEnabled) return;
  const eventName = safeEventName(name);
  const eventParams = safeParams(params);
  if (isNative()) {
    await FirebaseAnalytics.logEvent({ name: eventName, params: eventParams }).catch(() => undefined);
    return;
  }
  const analytics = await getWebAnalytics();
  if (!analytics) return;
  try {
    logWebEvent(analytics, eventName, eventParams);
  } catch {
    // Analytics must never interrupt the product flow.
  }
}

export async function logScreenView(screen: string, source = "navigation"): Promise<void> {
  await logTechnicalEvent("mundely_screen_view", {
    screen: String(screen || "unknown").slice(0, 80),
    source
  });
}

export async function logUserAction(
  action: string,
  feature: string,
  result = "ok",
  extra?: Record<string, string | number | boolean>
): Promise<void> {
  await logTechnicalEvent("mundely_user_action", {
    action: String(action || "unknown").slice(0, 80),
    feature: String(feature || "unknown").slice(0, 80),
    result: String(result || "unknown").slice(0, 80),
    ...(extra || {})
  });
}

export async function startAnalyticsSession(plan = "unknown"): Promise<void> {
  if (!analyticsConsentEnabled || sessionStarted) return;
  sessionStarted = true;
  sessionStartedAt = Date.now();
  lastLifecycleState = "foreground";
  await logTechnicalEvent("mundely_session_start", {
    plan,
    source: "app"
  });
}

export async function logAppLifecycle(state: "foreground" | "background"): Promise<void> {
  if (!analyticsConsentEnabled || state === lastLifecycleState) return;
  lastLifecycleState = state;
  if (state === "foreground") {
    await logTechnicalEvent("mundely_app_foreground", { source: "lifecycle" });
    return;
  }
  const elapsed = sessionStartedAt ? Math.max(0, Date.now() - sessionStartedAt) : 0;
  await logTechnicalEvent("mundely_app_background", {
    source: "lifecycle",
    duration_bucket: durationBucket(elapsed)
  });
}

export async function logFeatureResult(
  feature: string,
  action: string,
  result: "ok" | "error" | "cancelled" | "pending" | "unavailable",
  extra?: Record<string, string | number | boolean>
): Promise<void> {
  await logUserAction(action, feature, result, extra);
}

export async function reportHandledError(error: unknown, context?: string): Promise<void> {
  if (!isNative()) return;
  const message = error instanceof Error ? error.message : String(error || "Unknown error");
  const sanitized = [context, message].filter(Boolean).join(": ").slice(0, 500);
  await FirebaseCrashlytics.recordException({ message: sanitized }).catch(() => undefined);
}

export async function setCrashReportingConsent(enabled: boolean): Promise<void> {
  if (!isNative()) return;
  await FirebaseCrashlytics.setEnabled({ enabled }).catch(() => undefined);
}
